import { useEffect, useRef } from 'react';

type Flag = { current: boolean };

/**
 * The mount/cleanup pair behind `useCancelledOnUnmount`, standalone so the
 * contract that matters can be tested without a renderer.
 *
 * The reset on the way in is the whole point. React's StrictMode runs mount,
 * cleanup, mount in development. An effect that only ever raises the flag has
 * it raised by that first cleanup and never lowered again, so every loop
 * guarded by it exits before its first iteration: the work is requested, and
 * its result is never collected.
 */
export function cancellationEffect(flag: Flag): () => () => void {
  return () => {
    flag.current = false;
    return () => { flag.current = true; };
  };
}

/**
 * False while the component is on screen, true once it has gone. Long-running
 * loops check it before they wait again or write state.
 */
export function useCancelledOnUnmount(): Flag {
  const cancelled = useRef(false);
  useEffect(cancellationEffect(cancelled), []);
  return cancelled;
}
